import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { Column, usePagination, useSortBy, useTable } from 'react-table';

import { getDefaultPageSize, sortStrings } from "../../../helpers/utils";

import TableCSVExport from '../../TableCSVExport';
import ReactTable from '../../ReactTable';
import SearchInput from '../../SearchInput';
import HealthBadge from '../HealthBadge';

import ClustersDataTable from '../../../types/cluster/tables/ClustersDataTable';

import { DATA_LOADING, SEVERITY_CLASS_NAMES } from "../../Constants";
import { TimeRangeContext } from "../../../context/TimeRangeContext";
import { buildRedirectQuery } from "../../../helpers/timeRangeUtils";

function ClustersTable(props: { loading: number, clustersDataTable: ClustersDataTable[] }) {
    const navigate = useNavigate();
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const [tableFilter, setTableFilter] = useState(query.get("filter") || "");
    const initialPageIndex = Number(query.get("page") || 0);
    const initialPageSize = Number(query.get("pageSize") || getDefaultPageSize());
    const timeRangeContext = useContext(TimeRangeContext)
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus({ preventScroll: true });
        }
    }, []);

    function clearTableFilter() {
        setTableFilter('');
    }

    const data = useMemo(() => props.clustersDataTable.filter(row =>
        row.name.toLowerCase().includes(tableFilter.toLowerCase())), [props.clustersDataTable, tableFilter]);

    const columns: Column[] = useMemo(() => [
        {
            accessor: 'id',
            disableSortBy: true
        },
        {
            Header: 'Cluster',
            accessor: 'name',
            className: 'fw-bold col-width-30',
            defaultCanSort: true,
            sortType: sortStrings,
            Cell: ({ cell }) => (
                <React.Fragment>
                    <i className="fal fa-circle-nodes fa-fw me-1" aria-hidden="true"/>
                    <Link to={`/clusters/${cell.row.values.id}?${buildRedirectQuery(timeRangeContext.timeRange)}`}>{cell.row.values.name}</Link>
                </React.Fragment>
            )
        },
        {
            Header: 'Type',
            accessor: 'type',
            className: 'col-width-10 text-nowrap',
            defaultCanSort: true,
            sortType: 'alphanumeric',
            Cell: ({ cell }) => (
                <span data-tooltip-id="global-tooltip"
                      data-tooltip-content="Windows Server Failover Cluster (SQL Server Always On)">
                    <i className="icon icon-mssql me-1" aria-hidden="true"/>
                    {cell.row.values.type === 'wsfc' ? 'SQL Server Always On' : cell.row.values.type}
                </span>
            )
        },
        {
            Header: 'Health',
            accessor: 'health',
            className: 'col-width-10',
            defaultCanSort: true,
            sortType: 'alphanumeric',
            Cell: ({ cell }) => (<HealthBadge value={cell.row.values.health}/>)
        },
        {
            Header: 'Availability Groups',
            accessor: 'availabilitygroups',
            headerClassName: 'text-end',
            className: 'text-end col-width-10',
            defaultCanSort: true,
            sortType: 'basic',
        },
        {
            Header: 'Nodes',
            accessor: 'nodes',
            headerClassName: 'text-end',
            className: 'text-end col-width-10',
            defaultCanSort: true,
            sortType: 'basic',
        },
        {
            Header: 'Listeners',
            accessor: 'listeners',
            className: 'col-width-20',
            defaultCanSort: true,
            sortType: sortStrings,
        },
        {
            Header: 'Alerts',
            accessor: 'alerts',
            headerClassName: 'text-end',
            className: 'text-end col-width-10',
            defaultCanSort: true,
            sortType: 'basic',
            Cell: ({ cell }) => {
                const row = cell.row.original as ClustersDataTable;
                if (row.alerts === 0) {
                    return <span>0</span>;
                }
                return (
                    <span>
                        <i className={`fal fa-exclamation-triangle fa-fw me-1 ${SEVERITY_CLASS_NAMES[row.worstseverity] ?? ''}`}
                           aria-hidden="true"/>
                        {row.alerts}
                    </span>
                );
            }
        },
    ], [timeRangeContext]);

    const tableInstance = useTable({
            columns,
            data,
            initialState: {
                hiddenColumns: ['id'],
                pageIndex: initialPageIndex,
                pageSize: initialPageSize, sortBy: [{ id: 'name', desc: false }]
            },
            autoResetPage: false,
            autoResetSortBy: false,
            autoResetFilters: false,
            autoResetSelectedRows: false,
            autoResetExpanded: false,
            autoResetGroupBy: false,
        },
        useSortBy, usePagination);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize }
    } = tableInstance;

    useEffect(() => {
        const params = new URLSearchParams(location.search);

        if (tableFilter) {
            params.set('filter', tableFilter);
        } else {
            params.delete('filter');
        }

        params.set('page', pageIndex.toString());
        params.set('pageSize', pageSize.toString());

        navigate({ search: params.toString() }, { replace: true });
    }, [tableFilter, pageIndex, pageSize]);

    useEffect(() => {
        if (tableFilter) {
            gotoPage(0);
        }
    }, [tableFilter]);

    return (
        <React.Fragment>
            <div className="row row-cols-1 row-cols-md-2 table-search">
                <div className="col col-md-10">
                    <TableCSVExport
                        tableInstance={tableInstance}
                        filename="Clusters"
                    />
                </div>
                <div className="col col-md-2">
                    <SearchInput
                        ref={inputRef}
                        value={tableFilter}
                        onChange={(e) => setTableFilter(e.target.value)}
                        onClear={clearTableFilter}
                    />
                </div>
            </div>
            <ReactTable
                getTableProps={getTableProps}
                getTableBodyProps={getTableBodyProps}
                headerGroups={headerGroups}
                prepareRow={prepareRow}
                page={page}
                canPreviousPage={canPreviousPage}
                canNextPage={canNextPage}
                pageOptions={pageOptions}
                pageCount={pageCount}
                gotoPage={gotoPage}
                nextPage={nextPage}
                previousPage={previousPage}
                setPageSize={setPageSize}
                pageIndex={pageIndex}
                pageSize={pageSize}
                dataLength={data.length}
                error={null}
                loading={props.loading === DATA_LOADING}
            />
        </React.Fragment>
    );
}

export default ClustersTable;
